import React, { useState, useRef } from "react";
import axios from "axios";
import { Download, Send, Loader2, CheckCircle2, ScanLine, Sparkles, ChevronDown, Check, MapPin } from "lucide-react";
import { brochureOptions, formOptions } from "../mock";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "./ui/command";

// Empty when no backend is configured: requests then go to a relative /api
// path and fail cleanly into the existing error handling, instead of
// hitting "undefined/api".
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const empty = {
  company: "",
  person: "",
  mobile: "",
  email: "",
  city: "",
  country: "",
  business_type: "",
  interested_in: [],
  quantity: "",
  remark: "",
};

// Shared lead-capture form used by both the Brochure modal and the Contact section.
export default function LeadForm({ source = "website_enquiry", withDownload = false, submitLabel = "Send Enquiry", onDone }) {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const fileRef = useRef(null);

  const onCardUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/scan-card`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = res.data?.fields || {};
      if (res.data?.error && !Object.values(f).some(Boolean)) {
        setScanMsg("Couldn't read the card \u2014 please fill the form manually.");
      } else {
        setForm((prev) => ({
          ...prev,
          company: f.company || prev.company,
          person: f.person || prev.person,
          mobile: f.mobile || prev.mobile,
          email: f.email || prev.email,
          city: f.city || prev.city,
          country: f.country || prev.country,
        }));
        setScanMsg("Card scanned \u2014 details filled in. Please review before submitting.");
      }
    } catch (err) {
      setScanMsg("Couldn't read the card \u2014 please fill the form manually.");
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleInterest = (item) => {
    setForm((f) => ({
      ...f,
      interested_in: f.interested_in.includes(item)
        ? f.interested_in.filter((x) => x !== item)
        : [...f.interested_in, item],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.person || !form.mobile || !form.email) return;
    try {
      setStatus("loading");
      await axios.post(`${API}/leads`, { ...form, source });
      if (withDownload) {
        const res = await axios.get(`${API}/brochure`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "KSE-Inc-Company-Brochure.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
      setStatus("done");
      setForm(empty);
      onDone?.();
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const field = (key, label, type, required, placeholder) => (
    <div>
      <label className="text-sm font-medium text-slate-600 mb-1.5 block">
        {label} {required && <span className="text-[#2b3aa0]">*</span>}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2b3aa0]/40 focus:border-[#2b3aa0]"
      />
    </div>
  );

  if (status === "done") {
    return (
      <div className="text-center py-8">
        <CheckCircle2 className="text-green-500 mx-auto mb-4" size={52} />
        <h4 className="text-2xl font-bold text-[#1a2a6b] mb-2">Thank you!</h4>
        <p className="text-slate-500">
          {withDownload
            ? "Your brochure download has started. Our team will reach out shortly."
            : "Your enquiry has been received. Our team will get back to you shortly."}
        </p>
        <button onClick={() => setStatus("idle")} className="kse-btn bg-[#2b3aa0] text-white px-8 py-3 mt-6 mx-auto">
          Submit Another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Upload business card to auto-fill */}
      <div className="rounded-2xl border-2 border-dashed border-[#2b3aa0]/30 bg-blue-50/50 p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2b3aa0]/10 flex items-center justify-center shrink-0">
            <Sparkles size={20} className="text-[#2b3aa0]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1a2a6b]">Skip the typing</p>
            <p className="text-xs text-slate-500">Upload your business card and we'll fill it in for you.</p>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onCardUpload} className="hidden" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={scanning}
          className="kse-btn bg-[#1a2a6b] hover:bg-[#2b3aa0] text-white px-4 py-2.5 text-sm shrink-0 disabled:opacity-70"
        >
          {scanning ? <><Loader2 size={16} className="animate-spin" /> Reading...</> : <><ScanLine size={16} /> Upload Card</>}
        </button>
      </div>
      {scanMsg && <p className="text-xs text-[#2b3aa0] font-medium -mt-1">{scanMsg}</p>}

      <div className="grid sm:grid-cols-2 gap-4">
        {field("company", "Company Name", "text", true, "Your company")}
        {field("person", "Contact Person", "text", true, "Full name")}
        {field("mobile", "Mobile Number", "tel", true, "+91 ...")}
        {field("email", "Email ID", "email", true, "you@company.com")}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* City typeahead */}
        <div>
          <label className="text-sm font-medium text-slate-600 mb-1.5 block">City</label>
          <Popover open={cityOpen} onOpenChange={setCityOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-[#2b3aa0]/40 focus:border-[#2b3aa0]"
              >
                <span className={form.city ? "text-slate-800" : "text-slate-400"}>
                  {form.city || "Select or type city"}
                </span>
                <ChevronDown size={16} className="text-slate-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
              <Command>
                <CommandInput
                  placeholder="Type a city..."
                  value={form.city}
                  onValueChange={(v) => setForm((f) => ({ ...f, city: v }))}
                />
                <CommandList>
                  <CommandEmpty className="py-3 px-4 text-sm text-slate-500">
                    Press to use "{form.city}"
                  </CommandEmpty>
                  <CommandGroup>
                    {formOptions.cities.map((c) => (
                      <CommandItem key={c} value={c} onSelect={() => { setForm((f) => ({ ...f, city: c })); setCityOpen(false); }}>
                        <MapPin size={14} className="mr-2 text-[#2b3aa0]" />
                        {c}
                        {form.city === c && <Check size={14} className="ml-auto text-[#2b3aa0]" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Country select */}
        <div>
          <label className="text-sm font-medium text-slate-600 mb-1.5 block">Country</label>
          <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
            <SelectTrigger className="w-full rounded-xl border-slate-200 py-2.5 h-auto focus:ring-2 focus:ring-[#2b3aa0]/40">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {formOptions.countries.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Business type select */}
      <div>
        <label className="text-sm font-medium text-slate-600 mb-1.5 block">Are you a...</label>
        <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
          <SelectTrigger className="w-full rounded-xl border-slate-200 py-2.5 h-auto focus:ring-2 focus:ring-[#2b3aa0]/40">
            <SelectValue placeholder="Retailer, Wholesaler, Distributor, Chain Store, Online B2B / B2C..." />
          </SelectTrigger>
          <SelectContent>
            {formOptions.businessTypes.map((b) => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-600 mb-2 block">Interested In</label>
        <div className="flex flex-wrap gap-2">
          {brochureOptions.interests.map((opt) => {
            const active = form.interested_in.includes(opt);
            return (
              <button
                type="button"
                key={opt}
                onClick={() => toggleInterest(opt)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  active
                    ? "bg-[#2b3aa0] text-white border-[#2b3aa0]"
                    : "bg-white text-[#1a2a6b] border-slate-200 hover:border-[#2b3aa0]"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {field("quantity", "Approx. Order Quantity (pcs)", "text", false, "e.g. 5,000")}
        {field("remark", "Remark", "text", false, "Any specific requirement")}
      </div>

      {status === "error" && <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>}

      <button
        type="submit"
        disabled={status === "loading"}
        className="kse-btn w-full justify-center bg-[#2b3aa0] hover:bg-[#1a2a6b] text-white py-3.5 disabled:opacity-70"
      >
        {status === "loading" ? (
          <><Loader2 size={18} className="animate-spin" /> {withDownload ? "Preparing brochure..." : "Sending..."}</>
        ) : (
          <>{submitLabel} {withDownload ? <Download size={18} /> : <Send size={17} />}</>
        )}
      </button>
    </form>
  );
}
